/** Reports: production breakdowns, agent performance and claims ageing, with export. */

import { Download } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChartCard, ChartTable, useChartColors } from '@/components/charts/primitives';
import { EmptyState, PageHeader } from '@/components/common/page-header';
import { FilterBar } from '@/components/filters/filter-bar';
import { download } from '@/lib/api/client';
import {
  useAgentPerformance,
  useBreakdown,
  useClaimsAgeing,
  useInsuranceCompanies,
} from '@/lib/api/domains';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useListState } from '@/hooks/use-list-state';

export function ReportsPage() {
  const { t, money, number, percent } = useI18n();
  const { can } = useAuth();
  const colors = useChartColors();

  const list = useListState();
  const filters = list.filters;

  const byCompany = useBreakdown('company', filters);
  const byType = useBreakdown('insurance-type', filters);
  const agents = useAgentPerformance(filters);
  const ageing = useClaimsAgeing();
  const { data: insurers } = useInsuranceCompanies();

  return (
    <>
      <PageHeader
        title={t('report.title')}
        description={t('report.subtitle')}
        actions={
          can('report:export') && (
            <Button onClick={() => void download('/dashboard/export', 'report.xlsx', filters)}>
              <Download /> {t('action.export')}
            </Button>
          )
        }
      />

      <FilterBar
        className="mb-5"
        listState={list}
        filters={[
          { key: 'dateFrom', type: 'date', label: t('common.from') },
          { key: 'dateTo', type: 'date', label: t('common.to') },
          {
            key: 'insuranceCompanyId',
            type: 'options',
            label: t('policy.company'),
            options: (insurers ?? []).map((insurer) => ({ value: insurer.id, label: insurer.name })),
          },
        ]}
      />

      <div className="grid gap-4 xl:grid-cols-2">
        <RankedCard
          title={t('report.byCompany')}
          rows={byCompany.data ?? []}
          isLoading={byCompany.isLoading}
          color={colors.series1}
        />
        <RankedCard
          title={t('report.byType')}
          rows={(byType.data ?? []).map((row) => ({ ...row, label: t(`enum.insuranceType.${row.key}`) }))}
          isLoading={byType.isLoading}
          color={colors.series1}
        />

        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle>{t('report.agentPerformance')}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {agents.isLoading ? (
              <Skeleton className="m-5 h-40" />
            ) : !agents.data?.length ? (
              <EmptyState title={t('common.empty')} compact />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('client.agent')}</TableHead>
                    <TableHead className="text-right">{t('report.policyCount')}</TableHead>
                    <TableHead className="text-right">{t('dashboard.totalPremium')}</TableHead>
                    <TableHead className="text-right">{t('dashboard.totalCommission')}</TableHead>
                    <TableHead className="text-right">{t('report.renewalsCompleted')}</TableHead>
                    <TableHead className="text-right">{t('report.claimsHandled')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agents.data.map((row) => (
                    <TableRow key={row.agentId}>
                      <TableCell className="py-2.5 font-medium">{row.agentName}</TableCell>
                      <TableCell className="py-2.5 text-right tabular">{number(row.policyCount)}</TableCell>
                      <TableCell className="py-2.5 text-right tabular">{money(row.premium)}</TableCell>
                      <TableCell className="py-2.5 text-right font-medium tabular">
                        {money(row.commission)}
                      </TableCell>
                      <TableCell className="text-muted-foreground py-2.5 text-right tabular">
                        {number(row.renewalsCompleted)}
                      </TableCell>
                      <TableCell className="text-muted-foreground py-2.5 text-right tabular">
                        {number(row.claimsHandled)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <ChartCard
          className="xl:col-span-2"
          title={t('report.claimsAgeing')}
          isLoading={ageing.isLoading}
          isEmpty={!ageing.data?.length}
          height={180}
          table={
            <ChartTable
              columns={[t('claim.daysOpen'), t('common.total'), t('claim.amount')]}
              rows={(ageing.data ?? []).map((row) => [row.bucket, row.count, money(row.amount)])}
            />
          }
        >
          {/*
            The bars are sized in percent, so every ancestor between them and ChartCard's
            fixed height has to keep that height definite. This row used to be
            `items-end`, which is align-items:flex-end — so the columns did NOT stretch to
            the row, they shrank to their own text, and a percentage height resolved
            against `auto` is indefinite. The bars computed to zero and the chart rendered
            as three numbers floating over three labels.

            The columns now stretch (default align-items) and each reserves the leftover
            space between its count and its label as a `flex-1` track. That track has a
            definite height, which is what the bar's percentage needs, and `items-end` on
            the track is what stands the bar on the axis instead of hanging it from the top.
          */}
          <div className="flex h-full gap-3">
            {(ageing.data ?? []).map((bucket, index) => {
              const max = Math.max(1, ...(ageing.data ?? []).map((row) => row.count));
              // Ageing is ordinal, so the ramp darkens with the bucket rather than
              // switching hue — older is heavier, not "a different category".
              const opacity = 0.35 + (index / Math.max(1, (ageing.data?.length ?? 1) - 1)) * 0.65;

              return (
                <div key={bucket.bucket} className="flex h-full flex-1 flex-col items-center gap-1.5">
                  <span className="text-xs font-medium tabular">{bucket.count}</span>
                  <div className="flex w-full flex-1 items-end">
                    <div
                      className="w-full rounded-t-[4px]"
                      style={{
                        // The 4% floor keeps a zero bucket visible as a stub rather than
                        // letting it vanish and read as a missing category.
                        height: `${Math.max(4, (bucket.count / max) * 100)}%`,
                        backgroundColor: colors.series1,
                        opacity,
                      }}
                    />
                  </div>
                  <span className="text-muted-foreground text-xs">{bucket.bucket}</span>
                </div>
              );
            })}
          </div>
        </ChartCard>
      </div>
    </>
  );
}

function RankedCard({
  title,
  rows,
  isLoading,
  color,
}: {
  title: string;
  rows: { key: string; label: string; premium: string; commission: string; count: number }[];
  isLoading: boolean;
  color?: string;
}) {
  const { t, money, moneyCompact } = useI18n();
  const max = Math.max(1, ...rows.map((row) => Number(row.premium)));

  return (
    <ChartCard
      title={title}
      isLoading={isLoading}
      isEmpty={!rows.length}
      height={300}
      fitContent
      table={
        <ChartTable
          columns={[title, t('report.policyCount'), t('dashboard.totalPremium'), t('dashboard.totalCommission')]}
          rows={rows.map((row) => [row.label, row.count, money(row.premium), money(row.commission)])}
        />
      }
    >
      <div className="flex flex-col gap-2">
        {/*
          The label track is `minmax(0,8rem)`, not a flat `8rem`. At a phone width a fixed
          128px label plus the 64px amount left the bar barely wider than its own corner
          radius; letting the label give way keeps the bar readable, and it is already
          truncating, so it loses an ellipsis rather than a word.
        */}
        {rows.slice(0, 10).map((row) => (
          <div key={row.key} className="grid grid-cols-[minmax(0,8rem)_1fr_auto] items-center gap-2">
            <span className="text-muted-foreground truncate text-xs" title={row.label}>
              {row.label}
            </span>
            <span className="bg-muted h-4 overflow-hidden rounded-sm">
              <span
                className="block h-full rounded-r-[4px]"
                style={{
                  width: `${Math.max(2, (Number(row.premium) / max) * 100)}%`,
                  backgroundColor: color,
                }}
              />
            </span>
            <span className="w-16 text-right text-xs font-medium tabular">
              {moneyCompact(row.premium)}
            </span>
          </div>
        ))}
      </div>
    </ChartCard>
  );
}
